import React from "react";
import { Facebook, Mail, Phone, MapPin, Heart } from "lucide-react";
import { Link } from "react-router-dom";
import { colors, contact, social, images, company } from "../config/site";
import NewsletterSignup from "./NewsletterSignup";

const QUICK_LINKS = [
  { to: "/", label: "Home" },
  { to: "/about", label: "About" },
  { to: "/development", label: "Development" },
  { to: "/programs", label: "Housing Stability Programs" },
  { to: "/partnerships", label: "Community Partnerships" },
  { to: "/resources", label: "Resources" },
  { to: "/contact", label: "Contact" },
];

const Footer = () => {
  return (
    <footer
      data-testid="site-footer"
      className="text-stone-100"
      style={{ backgroundColor: colors.primaryDark }}
    >
      <div className="container mx-auto max-w-7xl px-4 py-14">
        <div className="grid lg:grid-cols-4 md:grid-cols-2 gap-10">
          {/* Company Info */}
          <div className="space-y-5">
            <div className="flex items-center space-x-3">
              <img
                src={images.logo}
                alt={`${company.name} Logo`}
                className="h-11 w-auto rounded-md"
              />
              <div>
                <h3 className="text-lg font-bold text-white">{company.name}</h3>
                <p className="text-xs uppercase tracking-wider" style={{ color: colors.gold }}>
                  {company.tagline}
                </p>
              </div>
            </div>
            <p className="text-stone-300 text-sm leading-relaxed">
              Building housing. Strengthening families. Creating pathways to stability — for the families we serve
              through community-centered development and housing education.
            </p>
            <div className="flex items-center space-x-2" style={{ color: colors.gold }}>
              <Heart className="w-4 h-4" />
              <span className="text-xs uppercase tracking-wide font-semibold">Community First</span>
            </div>
          </div>

          {/* Quick Links */}
          <div className="space-y-5">
            <h4 className="text-base font-semibold text-white">Explore</h4>
            <ul className="space-y-2">
              {QUICK_LINKS.map((link) => (
                <li key={link.to}>
                  <Link
                    to={link.to}
                    className="text-sm text-stone-300 transition-colors duration-200 hover:text-white"
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* What We Do */}
          <div className="space-y-5">
            <h4 className="text-base font-semibold text-white">What We Do</h4>
            <ul className="space-y-2 text-sm text-stone-300">
              <li>Affordable Housing Development</li>
              <li>Property Rehabilitation</li>
              <li>Neighborhood Revitalization</li>
              <li>Housing Stability Education</li>
              <li>Tenant Success Programs</li>
              <li>Community Partnerships</li>
            </ul>
          </div>

          {/* Contact Info */}
          <div className="space-y-5">
            <h4 className="text-base font-semibold text-white">Get in Touch</h4>
            <div className="space-y-4 text-sm">
              <a href={contact.phoneHref} className="flex items-start space-x-3 text-stone-300 hover:text-white">
                <Phone className="w-4 h-4 mt-1 flex-shrink-0" style={{ color: colors.gold }} />
                <span>{contact.phone}</span>
              </a>
              <a href={contact.emailHref} className="flex items-start space-x-3 text-stone-300 hover:text-white break-all">
                <Mail className="w-4 h-4 mt-1 flex-shrink-0" style={{ color: colors.gold }} />
                <span>{contact.email}</span>
              </a>
              <div className="flex items-start space-x-3 text-stone-300">
                <MapPin className="w-4 h-4 mt-1 flex-shrink-0" style={{ color: colors.gold }} />
                <div>
                  <p>{contact.address.line1}</p>
                  <p>{contact.address.line2}</p>
                </div>
              </div>
              <a
                href={social.facebook}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center space-x-3 text-stone-300 hover:text-white"
              >
                <Facebook className="w-4 h-4 flex-shrink-0" style={{ color: colors.gold }} />
                <span>{social.facebookHandle}</span>
              </a>
            </div>
          </div>
        </div>

        {/* Newsletter — full width row, dashed gold border = placeholder for now */}
        <div className="mt-12">
          <NewsletterSignup />
        </div>

        {/* Bottom Bar */}
        <div className="border-t pt-6 mt-12" style={{ borderColor: "#4A6357" }}>
          <div className="flex flex-col md:flex-row justify-between items-center gap-3 text-xs text-stone-400">
            <p>© {new Date().getFullYear()} {company.legalName}. All rights reserved.</p>
            <div className="flex items-center gap-5">
              <span>Equal Housing Opportunity</span>
              <span>Women-Led Business</span>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
